import { useState } from "react";
import { Formik, Form, FieldArray } from "formik";
import PrimaryColorButton from "../../components/PrimaryColorButton";
import GradientButton from "../../components/GradientButton";
import TextInput from "../../components/TextInput";
import NumberInput from "../../components/NumberInput";
import EmailInput from "../../components/EmailInput";
import SelectInput from "../../components/SelectInput";
import { TeamMember, UserRole } from "@quantizr-engineering/common/types/user";
import { enumToOptions } from "../../utils/enumToOptions";
import Loader from "../../components/Loader";
import { useTeamService } from "../../api/teamService";
import AddButton from "../../components/AddButton";
import MinusButton from "../../components/MinusButton";
import SelectCell from "../../components/SelectCell";

const userRoleOptions = enumToOptions(UserRole);

const EditButtons = ({
  editMode,
  setEditMode,
  resetForm,
}: {
  editMode: boolean;
  setEditMode: (editMode: boolean) => void;
  resetForm: () => void;
}) => (
  <div className="container mx-auto py-8">
    {editMode ? (
      <div className="flex flex-row items-center space-x-4">
        <GradientButton type="submit" text="Save Changes" />
        <PrimaryColorButton
          type="button"
          text="Cancel"
          onClick={() => {
            resetForm();
            setEditMode(false);
          }}
        />
      </div>
    ) : (
      <GradientButton
        type="button"
        onClick={(e) => {
          e.preventDefault();
          setEditMode(true);
        }}
        text="Edit"
      />
    )}
  </div>
);

const EditableRow = ({
  member,
  editMode,
  handleChange,
  index,
  objectName,
  remove,
}: {
  member: Partial<TeamMember>;
  editMode: boolean;
  handleChange: (e: React.ChangeEvent<any>) => void;
  index: number;
  objectName: string;
  remove: any;
}) => (
  <tr className="cursor-pointer">
    <td className="py-2 px-4 border-b">
      {editMode ? (
        <div className="flex space-x-2">
          <TextInput
            name={`${objectName}.${index}.firstName`}
            value={member.firstName}
            onChange={handleChange}
          />
          <TextInput
            name={`${objectName}.${index}.lastName`}
            value={member.lastName}
            onChange={handleChange}
          />
        </div>
      ) : (
        <TextInput
          name={`${objectName}.${index}.firstName`}
          value={`${member?.firstName || ""} ${member?.lastName || ""}`}
          disabled
        />
      )}
    </td>
    <td className="py-2 px-4 border-b">
      {editMode ? (
        <EmailInput
          name={`${objectName}.${index}.email`}
          value={member.email}
          onChange={handleChange}
        />
      ) : (
        <EmailInput
          name={`${objectName}.${index}.email`}
          value={member?.email || ""}
          disabled
        />
      )}
    </td>
    <td className="py-2 px-4 border-b">
      <SelectCell
        name={`${objectName}.${index}.roles`}
        value={member.roles}
        options={userRoleOptions}
        onChange={handleChange}
        onBlur={() => {}}
        disabled={!editMode}
      />
    </td>
    {objectName === "teamMembers" && (
      <td className="py-2 px-4 border-b">
        {editMode ? (
          <NumberInput
            name={`${objectName}[${index}].commissionPercent`}
            value={member.commissionPercent as number}
            onChange={handleChange}
          />
        ) : (
          <NumberInput
            name={`${objectName}.${index}.commissionPercent`}
            value={member.commissionPercent || 0}
            disabled
          />
        )}
      </td>
    )}
    {editMode && (
      <td>
        <MinusButton onClick={() => remove(index)} />
      </td>
    )}
  </tr>
);

const TeamMembersTable = ({
  members,
  editMode,
  push,
  remove,
  handleChange,
}: {
  members: Partial<TeamMember>[];
  editMode: boolean;
  push: any;
  remove: any;
  handleChange: (e: React.ChangeEvent<any>) => void;
}) => (
  <>
    <table className="min-w-full bg-white border border-gray-300">
      <thead>
        <tr className="bg-gray-200 text-gray-700">
          <th className="py-2 px-4 border-b">Name</th>
          <th className="py-2 px-4 border-b">Email</th>
          <th className="py-2 px-4 border-b">Roles</th>
          <th className="py-2 px-4 border-b">Commision %</th>
        </tr>
      </thead>
      <tbody>
        {members.map((member, index) => (
          <>
            <EditableRow
              key={`teamMember${index}`}
              member={member}
              editMode={editMode}
              handleChange={handleChange}
              index={index}
              remove={remove}
              objectName={"teamMembers"}
            />
          </>
        ))}
        {members.length === 0 && (
          <tr>
            <td colSpan={4} className="text-center py-4 text-gray-500">
              No team members found
            </td>
          </tr>
        )}
      </tbody>
    </table>
    {editMode && (
      <div className="flex justify-end bg-white border-x border-b border-gray-300">
        <AddButton
          onClick={() => {
            push({});
          }}
        />
      </div>
    )}
  </>
);

const ArtistsTable = ({
  members,
  editMode,
  push,
  remove,
  handleChange,
}: {
  members: Partial<TeamMember>[];
  editMode: boolean;
  push: any;
  remove: any;
  handleChange: (e: React.ChangeEvent<any>) => void;
}) => (
  <>
    <table className="min-w-full bg-white border border-gray-300">
      <thead>
        <tr className="bg-gray-200 text-gray-700">
          <th className="py-2 px-4 border-b">Name</th>
          <th className="py-2 px-4 border-b">Email</th>
          <th className="py-2 px-4 border-b">Roles</th>
        </tr>
      </thead>
      <tbody>
        {members.map((member, index) => (
          <EditableRow
            key={`artist${index}`}
            member={member}
            editMode={editMode}
            handleChange={handleChange}
            index={index}
            remove={remove}
            objectName={"artists"}
          />
        ))}
        {members.length === 0 && (
          <tr>
            <td colSpan={2} className="text-center py-4 text-gray-500">
              No artists found
            </td>
          </tr>
        )}
      </tbody>
    </table>
    {editMode && (
      <div className="flex justify-end bg-white border-x border-b border-gray-300">
        <AddButton
          onClick={() => {
            push({});
          }}
        />
      </div>
    )}
  </>
);

const AllMembers = () => {
  const [error, setError] = useState("");
  const [editMode, setEditMode] = useState(false);
  const { teamMembers, artists, patchTeamMembers, teamMembersLoading } =
    useTeamService();

  const handleSubmit = (values: {
    teamMembers: Partial<TeamMember>[];
    artists: Partial<TeamMember>[];
  }) => {
    setEditMode(false);
    patchTeamMembers([...values.teamMembers, ...values.artists]);
  };

  if (teamMembersLoading) return <Loader />;
  if (error) return <p>{error}</p>;
  if (!teamMembers || !artists) return <p>No team members found.</p>;

  return (
    <div className="bg-gray-100 min-h-screen">
      <Formik initialValues={{ 
        teamMembers: teamMembers.map(t => { return {...t, roles: t.roles?.[0] ?? ''}}),
       artists: artists.map(t => { return {...t, roles: t.roles?.[0] ?? ''}}) }} onSubmit={handleSubmit}>
        {({ values, resetForm, handleChange }) => {
          return (
            <Form className="px-6">
              <EditButtons
                editMode={editMode}
                setEditMode={setEditMode}
                resetForm={resetForm}
              />

              <section>
                <h1 className="p-6 text-2xl font-bold mb-4">Team Members</h1>
                <FieldArray name="teamMembers">
                  {({ remove, push }) => (
                    <TeamMembersTable
                      members={values.teamMembers}
                      editMode={editMode}
                      handleChange={handleChange}
                      push={push}
                      remove={remove}
                    />
                  )}
                </FieldArray>
              </section>

              <section>
                <h1 className="p-6 text-2xl font-bold mb-4">Artists</h1>
                <FieldArray name="artists">
                  {({ remove, push }) => (
                    <ArtistsTable
                      members={values.artists}
                      editMode={editMode}
                      handleChange={handleChange}
                      push={push}
                      remove={remove}
                    />
                  )}
                </FieldArray>
              </section>
            </Form>
          );
        }}
      </Formik>
    </div>
  );
};

export default AllMembers;
